import queryString from 'query-string';
import { useEffect, useRef } from 'react';
import { SetURLSearchParams, useSearchParams } from 'react-router';

class URLParamStorage implements Storage {
    public setSearchParams: SetURLSearchParams | undefined;

    constructor() {
        this.setSearchParams = undefined;
    }

    key(index: number): string | null {
        if (!window.location.search) return null;
        const parsedParams = queryString.parse(window.location.search, {
            arrayFormat: 'bracket',
        });
        return Object.keys(parsedParams)[index]?.toString() ?? null;
    }

    getItem(key: string): string | null {
        if (!window.location.search) return null;
        const parsedParams = queryString.parse(window.location.search, {
            arrayFormat: 'bracket',
            parseBooleans: true,
            parseNumbers: true,
        });
        if (!(key in parsedParams)) return null;
        const rawValue = parsedParams[key];
        if (rawValue == null) return null;
        if (typeof rawValue === 'string') {
            // If the URL value itself is JSON (object/array), return it as-is so callers can parse once.
            try {
                JSON.parse(rawValue);
                return rawValue;
            } catch {
                // Fall through to primitive string serialization.
            }
        }
        return JSON.stringify(rawValue);
    }

    setItem(key: string, value: string): void {
        const parsedParams = queryString.parse(window.location.search, {
            arrayFormat: 'bracket',
            parseBooleans: true,
            parseNumbers: true,
        });

        const parsedValue = JSON.parse(value);
        switch (typeof parsedValue) {
            case 'string':
            case 'number':
            case 'boolean':
                parsedParams[key] = parsedValue;
                break;
            case 'object':
                parsedParams[key] = value;
                break;
        }

        const updatedQuery = queryString.stringify(parsedParams, {
            arrayFormat: 'bracket',
            encode: true,
        });
        this.setSearchParams?.(updatedQuery);
    }

    removeItem(key: string): void {
        const parsedParams = queryString.parse(window.location.search, {
            arrayFormat: 'bracket',
            parseBooleans: true,
            parseNumbers: true,
        });
        delete parsedParams[key];
        const updatedQuery = queryString.stringify(parsedParams, {
            arrayFormat: 'bracket',
            encode: true,
        });
        this.setSearchParams?.(updatedQuery);
    }

    clear(): void {
        this.setSearchParams?.('');
    }

    get length(): number {
        if (!window.location.search) return 0;
        const parsedParams = queryString.parse(window.location.search, {
            arrayFormat: 'bracket',
        });
        return Object.keys(parsedParams).length;
    }
}

const useURLParamStorage = (): URLParamStorage => {
    // We use setSearch params rather than window.history.pushState to maintain react router state
    const [, setSearchParams] = useSearchParams();
    // We use a ref to store the URLParamStorage instance to avoid re-creating it on every render
    const urlParamStorageRef = useRef<URLParamStorage>(new URLParamStorage());

    // setSearchParams updates everytime the search params change, so we need to update the setSearchParams function on the URLParamStorage instance
    useEffect(() => {
        urlParamStorageRef.current.setSearchParams = setSearchParams;
    }, [setSearchParams]);

    // We return a stable reference to the URLParamStorage instance
    return urlParamStorageRef.current;
};

export { URLParamStorage, useURLParamStorage };
